/**
 * environment-helpers.ts — v4M13.4c pure helpers wired into the
 * detection pipeline + ship-movement pass to apply the non-damage
 * environment effects (LoS / invisibility / movement penalty) per
 * `docs/TERRAIN_FEATURES.md §1`.
 *
 * Three helpers, each consumed at exactly one site:
 *   - `isShipHiddenByEnvironment` — `advance-detection.ts` elevates
 *     `targetView.cloaked` when the target is inside any env with
 *     `invisibilityInside` (nebula) or `blocksLineOfSight`
 *     (asteroid-field). Existing detection gates short-circuit on
 *     cloak so no per-channel changes needed.
 *   - `rayCrossesBlockingEnvironment` — `advance-detection.ts` skips
 *     scanners whose line-of-sight to the target passes through any
 *     env with `blocksLineOfSight`. Implements the "nebula occlusion"
 *     mechanic from `TERRAIN_FEATURES.md §1.3` (fleets hide BEHIND
 *     nebulae, not just INSIDE them).
 *   - `environmentSpeedMultiplier` — `advance.ts:advanceShipMovement`
 *     multiplies the ship's per-tick speed by the product of
 *     `movementSpeedMultiplier` from every env containing the ship.
 *     Multiplicative stacking per §4.
 *
 * All three are pure (no state mutation, no allocation). Squared-
 * distance comparison everywhere keeps the inside-test free of
 * `Math.sqrt`.
 */

import {
  effectiveEnvironmentEffect,
  kindHasGameplayEffect,
} from './balance-environment';
import type {
  EnvironmentEntity,
  GameState,
  ShipInstance,
  SystemId,
} from '../state/types/index';

/**
 * Squared-distance check: is point `(px, py)` inside the env's
 * bounding circle? Inline-able by V8.
 */
function pointInsideEnv(
  px: number,
  py: number,
  env: EnvironmentEntity,
): boolean {
  const dx = px - env.sx;
  const dy = py - env.sy;
  return dx * dx + dy * dy <= env.radius * env.radius;
}

/**
 * Returns true when `ship` is inside any environment in its system
 * whose effect has `invisibilityInside` OR `blocksLineOfSight`.
 * Mid-jump ships (`systemId === null`) return false trivially —
 * they're in hyperspace, not subject to in-system terrain effects.
 *
 * `advance-detection.ts` calls this once per (team, target) pair
 * and elevates `targetView.cloaked` to true when the result is true,
 * which the existing `visionSees` / `scanSees` gates already
 * short-circuit on. No per-channel changes needed downstream.
 *
 * Both `invisibilityInside` (nebula §1.3) and `blocksLineOfSight`
 * (asteroid-field §1.1 + nebula §1.3) produce identical
 * "hidden-from-outside" behaviour for ships INSIDE the env. The
 * `blocksLineOfSight` flag additionally gates `rayCrosses…` for
 * ships behind the env.
 */
export function isShipHiddenByEnvironment(
  state: GameState,
  ship: ShipInstance,
): boolean {
  if (ship.systemId === null) return false;
  for (const env of state.environment.values()) {
    if (env.systemId !== ship.systemId) continue;
    if (!kindHasGameplayEffect(env.kind)) continue;
    const effect = effectiveEnvironmentEffect(env);
    if (!effect.invisibilityInside && !effect.blocksLineOfSight) continue;
    if (pointInsideEnv(ship.sx, ship.sy, env)) return true;
  }
  return false;
}

/**
 * Returns true when the segment from `(fromX, fromY)` to
 * `(toX, toY)` crosses any env in `systemId` whose effect has
 * `blocksLineOfSight`. Used by `advance-detection.ts` to gate
 * scanner→target line-of-sight per `TERRAIN_FEATURES.md §1.3`
 * (nebula occlusion for ships behind the env).
 *
 * Standard segment-vs-circle test: project `centre - from` onto
 * `(to - from) / |to - from|²`, clamp `t` to [0, 1], compute the
 * closest point on the segment, compare squared distance to `r²`.
 *
 * No exclusion for envs containing either endpoint — by design the
 * "ship inside" case is already handled by `isShipHiddenByEnvironment`
 * (the target gets `cloaked = true` and never reaches this gate);
 * the "scanner inside" case correctly hides outside targets from
 * inside-the-nebula observers (a reasonable reading of "nebula
 * blocks line of sight" — works both ways).
 */
export function rayCrossesBlockingEnvironment(
  state: GameState,
  systemId: SystemId,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
): boolean {
  const vx = toX - fromX;
  const vy = toY - fromY;
  const vLenSq = vx * vx + vy * vy;
  if (vLenSq <= 0) return false; // degenerate segment, no crossing
  for (const env of state.environment.values()) {
    if (env.systemId !== systemId) continue;
    if (!kindHasGameplayEffect(env.kind)) continue;
    const effect = effectiveEnvironmentEffect(env);
    if (!effect.blocksLineOfSight) continue;
    // Project centre onto segment, clamp to [0, 1].
    const ux = env.sx - fromX;
    const uy = env.sy - fromY;
    let t = (ux * vx + uy * vy) / vLenSq;
    if (t < 0) t = 0;
    else if (t > 1) t = 1;
    const closestX = fromX + t * vx;
    const closestY = fromY + t * vy;
    const dx = closestX - env.sx;
    const dy = closestY - env.sy;
    if (dx * dx + dy * dy <= env.radius * env.radius) return true;
  }
  return false;
}

/**
 * Product of `movementSpeedMultiplier` from every env containing
 * `ship`. Returns 1.0 when the ship is in no movement-penalty env
 * (the common case — keeps `advanceShipMovement` fast-path). Stacks
 * multiplicatively per `TERRAIN_FEATURES.md §4` (two overlapping
 * asteroid fields would compound to 0.25× speed; one belt + one
 * field hypothetically with belt 0.7× would be 0.35×).
 *
 * v4M13.4c only asteroid-field has a movement penalty defined
 * (0.5×). Future kinds can add `movementSpeedMultiplier` to their
 * defaults in `balance-environment.ts` without changing this helper.
 */
export function environmentSpeedMultiplier(
  state: GameState,
  ship: ShipInstance,
): number {
  if (ship.systemId === null) return 1; // mid-jump, irrelevant
  let mul = 1;
  for (const env of state.environment.values()) {
    if (env.systemId !== ship.systemId) continue;
    if (!kindHasGameplayEffect(env.kind)) continue;
    const effect = effectiveEnvironmentEffect(env);
    if (effect.movementSpeedMultiplier === undefined) continue;
    if (effect.movementSpeedMultiplier === 1) continue; // no-op
    if (pointInsideEnv(ship.sx, ship.sy, env)) {
      mul *= effect.movementSpeedMultiplier;
    }
  }
  return mul;
}
